import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const DatenschutzPage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen py-20"
      style={{ backgroundColor: 'var(--color-bg-primary)' }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        {/* Navigation */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 mb-10 transition-colors hover:opacity-80" 
          style={{ color: 'var(--color-text-muted)' }}
          aria-label="Zurück zur Startseite"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Zurück zur Startseite
        </Link>

        {/* Header */}
        <header className="mb-10">
          <h1 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Datenschutzerklärung
          </h1>
          <div className="h-px w-full" style={{ backgroundColor: 'var(--color-text-muted)', opacity: 0.2 }} />
        </header>

        {/* Content */}
        <main className="space-y-12" style={{ color: 'var(--color-text-secondary)', lineHeight: 1.75 }}>
          
          <section>
            <h2 className="font-serif text-2xl mb-4" style={{ color: 'var(--color-text-primary)' }}>1. Datenschutz auf einen Blick</h2>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Allgemeine Hinweise</h3>
            <p className="mb-4">
              Diese Datenschutzerklärung gibt einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
            </p>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Datenerfassung auf dieser Website</h3>
            <p>
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Die Kontaktdaten können Sie dem Impressum dieser Website entnehmen. Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen (z. B. im Kontaktformular). Andere technische Daten (IP-Adresse, Browser) werden automatisch durch unsere IT-Systeme erfasst.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl mb-4" style={{ color: 'var(--color-text-primary)' }}>2. Hosting</h2>
            <p className="mb-4">
              Wir hosten unsere Website bei <strong>Hostinger</strong>. Anbieter ist die Hostinger International Ltd., 61 Lordou Vironos Street, 6023 Larnaca, Zypern.
            </p>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Serverstandort Deutschland</h3>
            <p className="mb-2">
              Die Inhalte unserer Website werden auf Servern am Standort Deutschland gespeichert. Wir haben mit dem Anbieter einen Vertrag über Auftragsverarbeitung (AVV) gemäß Art. 28 DSGVO geschlossen, um die datenschutzkonforme Verarbeitung sicherzustellen.
            </p>
            <p className="text-sm italic opacity-80">
              Die Nutzung von Hostinger erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse an einer technisch fehlerfreien und sicheren Bereitstellung unserer Website.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl mb-4" style={{ color: 'var(--color-text-primary)' }}>3. Allgemeine Pflichtinformationen</h2>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Verantwortliche Stelle</h3>
            <address className="not-italic mb-6">
              Madlin Firme - malneudenken<br />
              c/o Autorenglück #37737<br />
              Albert-Einstein-Straße 47<br />
              02977 Hoyerswerda<br />
              E-Mail: <a href="mailto:kontakt@malneudenken.de" className="hover:underline" style={{ color: 'var(--color-link-base)' }}>kontakt@malneudenken.de</a>
            </address>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Ihre Rechte</h3>
            <p className="mb-2">Sie haben jederzeit das Recht:</p>
            <ul className="list-disc ml-6 mb-6 space-y-1">
              <li>Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten.</li>
              <li>Die Berichtigung oder Löschung dieser Daten zu verlangen.</li>
              <li>Eine erteilte Einwilligung zur Datenverarbeitung zu widerrufen.</li>
              <li>Sich bei der zuständigen Datenschutz-Aufsichtsbehörde zu beschweren.</li>
            </ul>

            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Widerspruchsrecht gegen die Datenerhebung in besonderen Fällen (Art. 21 DSGVO)</h3>
            <p className="text-sm italic opacity-80">
              Wenn die Datenverarbeitung auf Grundlage von Art. 6 Abs. 1 lit. e oder f DSGVO erfolgt, haben Sie jederzeit das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, gegen die Verarbeitung Ihrer personenbezogenen Daten Widerspruch einzulegen.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl mb-4" style={{ color: 'var(--color-text-primary)' }}>4. Datenerfassung auf dieser Website</h2>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Kontaktformular</h3>
            <p className="mb-2">
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
            </p>
            <p className="text-sm italic opacity-80 mb-6">
              Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO).
            </p>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Server-Log-Dateien</h3>
            <p className="mb-2">
              Der Provider der Seiten erhebt und speichert automatisch Informationen in Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt (Browsertyp, Betriebssystem, Referrer URL, Hostname, Uhrzeit, IP-Adresse). Diese Daten sind für den sicheren Betrieb technisch notwendig. 
            </p>
            <p className="text-sm italic opacity-80">
              Grundlage ist Art. 6 Abs. 1 lit. f DSGVO.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl mb-4" style={{ color: 'var(--color-text-primary)' }}>5. Technik und Sicherheit</h2>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Keine Cookies / Kein Tracking</h3>
            <p className="mb-6">
              Diese Website verwendet keine Analyse-Tools (wie Google Analytics) und keine Tracking-Cookies. Es werden lediglich technisch notwendige Daten verarbeitet, um die Seite anzuzeigen. Ein Cookie-Banner ist daher nicht erforderlich.
            </p>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>Lokale Einbindungen (Icons & Fonts)</h3>
            <p className="mb-6">
              Alle grafischen Elemente, Icons und Schriftarten sind lokal auf unserem Webserver eingebunden. Es findet kein Datenaustausch mit Drittanbietern statt.
            </p>
            
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>SSL- bzw. TLS-Verschlüsselung</h3>
            <p>
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie an der Adresszeile "https://" und dem Schloss-Symbol.
            </p>
          </section>
        </main>
      </div>
    </motion.div>
  );
};

export default DatenschutzPage;